import { MutableRefObject, useLayoutEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

type UseHeightResult<Element extends HTMLElement> = {
  ref: MutableRefObject<Nullable<Element>>;
  height: number;
};

/**
 * Returns a ref and the current height of the attached element.
 */
const useHeight = <Element extends HTMLElement = HTMLElement>(): UseHeightResult<Element> => {
  const ref = useRef<Nullable<Element>>(null);
  const [height, setHeight] = useState(0);

  const [resizeObserver] = useState(
    () =>
      new ResizeObserver(() => {
        if (!ref.current) {
          return;
        }

        const { offsetHeight } = ref.current;

        if (height !== offsetHeight) {
          setHeight(offsetHeight);
        }
      }),
  );

  useLayoutEffect(() => {
    if (ref.current) {
      setHeight(ref.current.offsetHeight);
      resizeObserver.observe(ref.current);
    }

    return () => resizeObserver.disconnect();
  }, [resizeObserver]);

  return { ref, height };
};

export default useHeight;
